import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Send } from 'react-feather';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { MAP_LANGUAGE_TO_MARKET } from 'constants/payment';
import { bookingShape } from 'constants/propTypes';
import { SEND_QUOTE_CONFIRMATION_EMAIL } from 'store/mutations';
import { SelectField } from 'admin-components/components';
import AlertBox from 'components/ui/AlertBox';
import { ActionButton } from 'components/ui/Button';
import Label from 'components/ui/Label';
import Modal from 'components/ui/Modal';
import { DEFAULT_LANGUAGE, LANGUAGE_KEY } from 'constants/booking';
import EmailLanguage from 'components/ui/EmailLanguage';
import { GROUP_EMAIL_QUOTE } from 'types/constants';

import { StyledActionButton } from '../Payments.styles';

const ALLOWED_LANGUAGES = ['EN', 'FR', 'NL'];

const SendQuoteConfirmationEmailButton = ({
  booking: { customer, passengers, reference: bookingReference, additionalDetails },
  quotationStatus,
  disabled,
  isReviewQuote = false
}) => {
  const [visible, setVisible] = useState(false);
  const customerEmail = customer?.email;

  const emails = [...new Set([customerEmail, ...passengers.map(({ email }) => email)].filter((email) => Boolean(email)))].sort();
  const emailAddressOptions = emails.map((value) => ({ label: value, value }));
  const defaultEmail = emails[0];
  const [emailAddress, setEmailAddress] = useState(defaultEmail);

  const bookingLanguage = additionalDetails?.find(({ key }) => key === LANGUAGE_KEY)?.value ?? DEFAULT_LANGUAGE;
  const defaultLanguage = ALLOWED_LANGUAGES.includes(bookingLanguage) ? bookingLanguage : DEFAULT_LANGUAGE;
  const [emailLanguage, setEmailLanguage] = useState(defaultLanguage);

  const [sent, setSent] = useState(false);
  const [success, setSuccess] = useState(false);

  const [sendQuoteConfirmationEmail, { data, loading, error }] = useMutation(SEND_QUOTE_CONFIRMATION_EMAIL, {
    variables: {
      bookingReference,
      emailAddress,
      market: MAP_LANGUAGE_TO_MARKET[emailLanguage],
      ...(isReviewQuote && { quoteType: GROUP_EMAIL_QUOTE })
    }
  });

  useEffect(() => {
    if (data) {
      const { sendQuoteConfirmationEmail } = data;
      setSent(true);
      setSuccess(sendQuoteConfirmationEmail);
    }
  }, [data]);

  const reset = () => {
    setVisible(false);
    setSent(false);
    setSuccess(false);
    setEmailAddress(defaultEmail);
    setEmailLanguage(defaultLanguage);
  };

  let alertMessage;
  let alertError = AlertBox.variants.ERROR;

  if (error) {
    alertMessage = 'sendQuoteConfirmationEmail.error';
  } else if (sent && !success) {
    alertMessage = 'sendQuoteConfirmationEmail.failure';
  } else if (sent && success) {
    alertMessage = 'sendQuoteConfirmationEmail.success';
    alertError = AlertBox.variants.SUCCESS;
  }

  return (
    <>
      <StyledActionButton
        onClick={() => setVisible(true)}
        data-cy="send-quote-confirmation-email-open"
        data-testid="send-quote-confirmation-email-open"
        disabled={disabled || !defaultEmail}
      >
        <Send />
        <FormattedMessage id={`sendQuoteConfirmationEmail.${quotationStatus}`} />
      </StyledActionButton>
      <Modal
        alert={
          alertMessage ? (
            <AlertBox dataCy="send-quote-confirmation-email-alert" data-testid="send-quote-confirmation-email-alert" variant={alertError}>
              <FormattedMessage id={alertMessage} />
            </AlertBox>
          ) : null
        }
        isOpen={visible}
        title={<FormattedMessage id="sendQuoteConfirmationEmail.title" />}
        onClose={() => reset()}
        bottomRow={
          <ActionButton
            loading={loading}
            data-cy="send-quote-confirmation-email-submit"
            data-testid="send-quote-confirmation-email-submit"
            onClick={() => sendQuoteConfirmationEmail()}
          >
            <FormattedMessage id="sendQuoteConfirmationEmail.submit" />
          </ActionButton>
        }
      >
        <>
          <div>
            <Label htmlFor="send-quote-confirmation-language-select">
              <FormattedMessage id="sendQuoteConfirmationEmail.market" />
            </Label>
            <EmailLanguage
              disabled={loading}
              id="send-quote-confirmation-market-select"
              data-testid="send-quote-confirmation-market-select"
              width="100%"
              onChange={(event) => setEmailLanguage(event.value)}
              filterOption={({ value }) => ALLOWED_LANGUAGES.includes(value)}
              initialValue={emailLanguage}
            />
          </div>
          <div>
            <Label htmlFor="send-quote-confirmation-email-select">
              <FormattedMessage id="sendQuoteConfirmationEmail.recipient" />
            </Label>
            <SelectField
              disabled={loading}
              id="send-quote-confirmation-email-select"
              data-testid="send-quote-confirmation-email-select"
              value={emailAddress}
              options={emailAddressOptions}
              onChange={(newEmailAddress) => setEmailAddress(newEmailAddress)}
            />
          </div>
        </>
      </Modal>
    </>
  );
};

SendQuoteConfirmationEmailButton.propTypes = {
  booking: bookingShape.isRequired,
  quotationStatus: PropTypes.oneOfType([PropTypes.string]),
  disabled: PropTypes.bool,
  isReviewQuote: PropTypes.bool
};

export default SendQuoteConfirmationEmailButton;
